import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useOrgClaims } from '@/lib/hooks/useOrgClaims';
import { useSwitchOrg } from '@/lib/hooks/useSwitchOrg';
import { buildOrgPath, extractOrgId, hasAuthCallbackParams, stripOrgPrefix } from '@/lib/utils/orgRoutes';

const SWITCH_TARGET_KEY = 'antioch_org_switch_target';
const SWITCH_TARGET_ID_KEY = 'antioch_org_switch_target_id';

/**
 * Keeps the active organization in sync with the URL. Renders nothing; owns all
 * org-URL normalization in a single effect:
 *
 * - **No org in the path** -> inject the current org, preserving the rest of
 *   the path and the query string.
 * - **A different, accessible org in the path** -> auto-switch to it via the
 *   refresh-token grant (a reload lands the user in the target org).
 * - **A different, inaccessible org** -> error toast + redirect to the current
 *   org.
 * - **Post-switch verification** -> after the switch reload, confirm the new
 *   token actually matches the requested org and toast success / failure. A
 *   failed switch (Auth0 silent-SSO drop) is caught by a loop guard so we never
 *   re-switch into an infinite reload.
 */
export default function OrgUrlSync() {
  const { isAuthenticated } = useAuth0();
  const { orgId: claimsOrgId, organizations, isLoading } = useOrgClaims();
  const location = useLocation();
  const navigate = useNavigate();
  const switchOrg = useSwitchOrg();
  // Guards against a second switch firing while the first is mid-flight (before
  // its reload commits) if the effect re-runs on a dependency change.
  const switchInFlightRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || isLoading) return;
    // Never rewrite the URL while Auth0 is completing a callback.
    if (hasAuthCallbackParams(location.search)) return;

    const urlOrg = extractOrgId(location.pathname);
    // Preserve the query string and fragment across every normalization
    // redirect (e.g. a legacy deep link /docs/welcome#installation).
    const rest = `${location.search}${location.hash}`;

    // A marker means we just returned from a switch attempt. Only verify (and
    // consume the marker) once claims give a definitive org. A transient
    // claims-fetch error leaves claimsOrgId null -- that's "unknown", not
    // "failed", so we keep the marker rather than fire a false failure and
    // permanently lose the success toast; a later successful claims load
    // verifies it.
    const justAttemptedId = sessionStorage.getItem(SWITCH_TARGET_ID_KEY);
    const justAttemptedName = sessionStorage.getItem(SWITCH_TARGET_KEY);
    if (justAttemptedId !== null && claimsOrgId) {
      sessionStorage.removeItem(SWITCH_TARGET_ID_KEY);
      sessionStorage.removeItem(SWITCH_TARGET_KEY);
      if (claimsOrgId === justAttemptedId) {
        toast.success(`Switched to ${justAttemptedName}`);
      } else {
        toast.error(
          `Failed to switch to ${justAttemptedName ?? 'organization'} -- still in current org. Try again or sign out and back in.`
        );
      }
    }

    // Can't normalize against an org we don't know yet.
    if (!claimsOrgId) return;

    // No org in the path -> inject the current org, preserving path + query.
    if (!urlOrg) {
      const base = location.pathname === '/' ? '/home' : location.pathname;
      navigate(`${buildOrgPath(claimsOrgId, base)}${rest}`, { replace: true });
      return;
    }

    // Bare org path (/org_X) -> land on home within that org.
    if (stripOrgPrefix(location.pathname) === '/') {
      navigate(`${buildOrgPath(urlOrg, '/home')}${rest}`, { replace: true });
      return;
    }

    // Already in the right org -- nothing to do.
    if (urlOrg === claimsOrgId) return;

    // We just tried to switch to this exact org and the token still doesn't
    // match (Auth0 silent-SSO drop). Don't loop -- fall back to the real org.
    if (justAttemptedId === urlOrg) {
      navigate(`${buildOrgPath(claimsOrgId, stripOrgPrefix(location.pathname))}${rest}`, { replace: true });
      return;
    }

    // Different org in the path. Only refuse up front when we have a populated
    // org list that definitively excludes this org. When the list is unknown
    // (`organizations` claim absent), attempt the switch and let Auth0 be the
    // authority -- a rejected grant falls back to the current-org URL below,
    // rather than wrongly telling a member they lack access.
    const target = organizations?.find((o) => o.id === urlOrg);
    if (organizations && !target) {
      toast.error(`You don't have access to organization ${urlOrg}`);
      navigate(`${buildOrgPath(claimsOrgId, stripOrgPrefix(location.pathname))}${rest}`, { replace: true });
      return;
    }
    // On a failed switch (revoked membership, expired refresh token, or an org
    // the token can't actually reach), the token still belongs to the current
    // org -- fall back to the current-org URL so we never leave the target-org
    // path resolving with the wrong token. The in-flight guard stops a
    // dependency-change re-run from firing a second grant + reload.
    if (switchInFlightRef.current) return;
    switchInFlightRef.current = true;
    void switchOrg(target ?? { id: urlOrg, display_name: urlOrg }).then((ok) => {
      if (!ok) {
        switchInFlightRef.current = false;
        navigate(`${buildOrgPath(claimsOrgId, stripOrgPrefix(location.pathname))}${rest}`, { replace: true });
      }
    });
  }, [
    isAuthenticated,
    isLoading,
    claimsOrgId,
    organizations,
    location.pathname,
    location.search,
    location.hash,
    navigate,
    switchOrg,
  ]);

  return null;
}
